import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchInbox,
  UnauthorizedError,
  type InboxRow,
} from "@/lib/clinician-api";
import type { PatientGroup } from "@care-by-ella/contracts";
import { PatientGroupBadge } from "@/components/PatientGroupBadge";
import { transferReviewStatusLabel } from "@/lib/state-labels";
import { useViewPath } from "@/components/SupportView";

function severityChip(outcome: InboxRow["outcome"], flagCount: number) {
  if (outcome === "screening_failed") {
    return (
      <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium bg-danger/10 text-danger border border-danger/30">
        Kontraindikation
      </span>
    );
  }
  if (flagCount > 0) {
    return (
      <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium bg-warning/10 text-warning border border-warning/30">
        {/* The plural changes the ending rather than appending one: "flagga"
            becomes "flaggor", not "flaggaor". */}
        {flagCount} {flagCount === 1 ? "flagga" : "flaggor"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium bg-success/10 text-success border border-success/30">
      Ren bedömning
    </span>
  );
}

function relative(ts: string | null) {
  if (!ts) return "—";
  const diffMs = Date.now() - new Date(ts).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins} min sedan`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} tim sedan`;
  const days = Math.round(hrs / 24);
  return `${days} d sedan`;
}

// TES-506 — the same table, without the name "Inkorg".
//
// The 2026-09-04 walkthrough: there were three different inboxes, and the ask
// was for no inbox at all. This page was one of them — but it is also the only
// surface that carries TES-458's journey filter and the assessment column, and
// those were requested, not an aggregation of our own. What was removed was the
// word and the page's role as a collection point: the page is now named for
// what it shows, and the counter sits in the menu.
export function InboxRoute() {
  const { signOut } = useAuth();
  const viewPath = useViewPath();
  const [rows, setRows] = useState<InboxRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TES-458 — the existing queue with marking and filtering,
  // not a separate queue for prescription transfers.
  const [group, setGroup] = useState<PatientGroup | "ALL">("ALL");
  // TES-487 (Ö-20) — approved prescription transfers are out of the queue by
  // default; the doctor can still list them, she never loses the patient.
  const [showApproved, setShowApproved] = useState(false);

  useEffect(() => {
    setRows(null);
    fetchInbox({ includeApproved: showApproved })
      .then((r) => setRows(r.rows))
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [signOut, showApproved]);

  const visible =
    rows === null
      ? null
      : group === "ALL"
        ? rows
        : rows.filter((r) => r.patientGroup === group);
  const transferCount =
    rows?.filter((r) => r.patientGroup === "PRESCRIPTION_TRANSFER").length ?? 0;
  // Undecided reviews — the part of the transfer queue that is actually the
  // doctor's to act on. Approved rows are only listed when asked for
  // (TES-487): the patient activates herself through the mailed link.
  const pendingTransferCount =
    rows?.filter((r) => r.transferReviewStatus === "pending_review").length ??
    0;

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">
          Hälsodeklarationer
        </h1>
        <span className="text-sm text-textSecondary">
          {visible ? `${visible.length} ärenden` : "Hämtar…"}
        </span>
      </div>

      {/* TES-458 — filter, not a separate queue. The count on each tab is the
          point: a doctor should see that transfer patients are waiting without
          having to switch to find out.
          TES-578 — while the prescription transfer is paused the question is
          not asked, so a permanent "Receptövertag (0, 0 att granska)" tab would
          only advertise a flow nobody can enter. The tabs appear once a
          transfer row exists (a patient who answered while the question was
          live). */}
      {rows && rows.length > 0 ? (
        <div className="flex gap-xs" role="group" aria-label="Filtrera på resa">
          {transferCount > 0
            ? (
                [
                  ["ALL", `Alla (${rows.length})`],
                  [
                    "PRESCRIPTION_TRANSFER",
                    `Receptövertag (${transferCount}, ${pendingTransferCount} att granska)`,
                  ],
                  [
                    "NEW_TREATMENT",
                    `Ny behandling (${rows.length - transferCount})`,
                  ],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setGroup(value)}
                  aria-pressed={group === value}
                  className={`text-xs font-medium rounded px-sm py-[6px] border transition ${
                    group === value
                      ? "bg-brand/10 border-brand/50 text-brand"
                      : "bg-surface border-surfaceMuted text-textSecondary hover:bg-surfaceMuted/40"
                  }`}
                >
                  {label}
                </button>
              ))
            : null}
          <label className="ml-auto flex items-center gap-xs text-xs text-textSecondary">
            <input
              type="checkbox"
              checked={showApproved}
              onChange={(e) => setShowApproved(e.target.checked)}
            />
            Visa godkända övertag
          </label>
        </div>
      ) : null}

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {visible && visible.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          {group === "ALL"
            ? "Inga nya hälsodeklarationer just nu."
            : "Inga patienter i den här gruppen just nu."}
        </div>
      ) : null}

      {visible && visible.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full min-w-[36rem] text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                <th className="text-left px-md py-sm font-medium">Patient</th>
                <th className="text-left px-md py-sm font-medium">Resa</th>
                <th className="text-left px-md py-sm font-medium">Bedömning</th>
                <th className="text-left px-md py-sm font-medium">Inlämnad</th>
                <th className="px-md py-sm" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={row.patientId}
                  className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                >
                  <td className="px-md py-sm">
                    <div>{row.name ?? "—"}</div>
                    <div className="font-mono text-xs text-textSecondary">
                      …{row.personalNumberLast6}
                    </div>
                  </td>
                  <td className="px-md py-sm">
                    <div className="flex flex-wrap items-center gap-xs">
                      <PatientGroupBadge group={row.patientGroup} />
                      {/* TES-458 — where her review stands; nothing until
                          F-07 is in. */}
                      {row.transferReviewStatus ? (
                        <span
                          className={`align-middle text-xs font-sans font-medium uppercase tracking-wide rounded border px-xs py-[2px] ${
                            transferReviewStatusLabel(row.transferReviewStatus)
                              .className
                          }`}
                        >
                          {
                            transferReviewStatusLabel(row.transferReviewStatus)
                              .label
                          }
                        </span>
                      ) : row.patientGroup === "PRESCRIPTION_TRANSFER" ? (
                        <span className="text-xs text-textSecondary">
                          F-07 ej inskickat
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-md py-sm">
                    {severityChip(row.outcome, row.flagCount)}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {relative(row.submittedAt)}
                  </td>
                  <td className="px-md py-sm text-right">
                    <Link
                      to={viewPath(`/patients/${row.patientId}`)}
                      className="text-brand hover:underline text-sm font-medium"
                    >
                      Öppna →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
